import { useRef } from 'react';
import { Badge, Table, Spinner, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchClientPortalOrders, CLIENT_PORTAL_PAGE_SIZE } from '../api/clientPortalApi';
import { ClientPortalLayout } from '../components/ClientPortalLayout';
import { TablePageLayout } from '../components/TablePageLayout';
import { formatCurrency, formatDate } from '../utils/formatters';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useScrollRestoration } from '../hooks/useScrollRestoration';

export function ClientPortalOrdersPage() {
  const loadMoreRef = useRef(null);

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetched,
  } = useInfiniteQuery({
    queryKey: ['client-portal-orders'],
    queryFn: fetchClientPortalOrders,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const currentCount = allPages.reduce((sum, page) => sum + page.results.length, 0);
      return lastPage.results.length === CLIENT_PORTAL_PAGE_SIZE ? currentCount : undefined;
    },
  });

  const orders = data?.pages.flatMap((page) => page.results) || [];

  const { containerRef, handleScroll } = useScrollRestoration(
    'clientPortalOrdersScroll',
    isFetched
  );

  useInfiniteScroll({
    loadMoreRef,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    rootRef: containerRef,
    dependencyArray: [orders.length]
  });

  return (
    <ClientPortalLayout>
      <TablePageLayout
        title="All orders"
        description="View all your orders."
        isLoading={isLoading && orders.length === 0}
        isError={isError}
        error={error}
        containerRef={containerRef}
        onScroll={handleScroll}
      >
        <Table responsive striped hover className="mb-0">
          <thead>
            <tr>
              <th>Order</th>
              <th>Company</th>
              <th>Order date</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && !isLoading ? (
              <tr>
                <td colSpan="4" className="text-center py-4 text-muted">
                  No orders found.
                </td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr key={order.name}>
                  <td>
                    <Link to={`/client/orders/${order.name}`}>{order.name}</Link>
                  </td>
                  <td>{order.partner?.name || '-'}</td>
                  <td>{formatDate(order.date_order)}</td>
                  <td>{formatCurrency(order.amount_total)}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        {(hasNextPage || isFetchingNextPage) && (
          <div ref={loadMoreRef} className="text-center py-4 border-top">
            {isFetchingNextPage ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Loading more...
              </>
            ) : (
              <Button variant="link" onClick={() => fetchNextPage()}>
                Load more
              </Button>
            )}
          </div>
        )}
      </TablePageLayout>
    </ClientPortalLayout>
  );
}
